import { useEffect, useRef } from 'react';
import { config } from '../config.ts';

interface TurnstileApi {
  render(el: HTMLElement, opts: Record<string, unknown>): string;
  remove(id: string): void;
}
declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let script: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  script ??= new Promise((resolve, reject) => {
    if (window.turnstile) return resolve(window.turnstile);
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.async = true;
    s.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('Turnstile failed to load.')));
    s.onerror = () => {
      script = null;
      reject(new Error('Turnstile failed to load. Check your connection.'));
    };
    document.head.appendChild(s);
  });
  return script;
}

/**
 * Cloudflare Turnstile widget (web attestation for the no-card trial, docs/research/05 §4).
 * `cData` carries the server challenge (POST /v1/attest/challenge), `action` is "trial".
 */
export function Turnstile({
  challenge,
  onToken,
  onError,
}: {
  challenge: string;
  onToken: (token: string) => void;
  onError: (message: string) => void;
}) {
  const el = useRef<HTMLDivElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: render once per challenge
  useEffect(() => {
    let id: string | null = null;
    let api: TurnstileApi | null = null;
    let alive = true;
    loadTurnstile()
      .then((t) => {
        if (!alive || !el.current) return;
        api = t;
        id = t.render(el.current, {
          sitekey: config.turnstileSiteKey,
          action: 'trial',
          cData: challenge,
          theme: 'dark',
          callback: (token: string) => onToken(token),
          'error-callback': () => onError('Verification failed. Please try again.'),
        });
      })
      .catch((e: Error) => onError(e.message));
    return () => {
      alive = false;
      if (api && id) api.remove(id);
    };
  }, [challenge]);
  return <div ref={el} className="min-h-[65px]" />;
}
